import {RcfaStoppageCode as StoppageCode} from '@domain/shared'
import {StoppageInfo} from '@domain/stoppages'
import {AnalysisPrivacyStatus, AnalysisType} from '@hconnect/common/types'
import {Icon} from '@hm/fundamental'
import {mdiCancel, mdiClockOutline, mdiNoteEditOutline} from '@mdi/js'
import moment from 'moment'
import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router'

import {FEATURE_NAMESPACE} from '../../localization'
import {usePlantUpmId} from '../common'

import type {CreateAnalysisFormProperties} from './CreateAnalysisForm'
import {
  CreateAnalysisItem,
  CreateAnalysisLocationState,
  StoppageOption
} from './createAnalysisForm.types'
import {useCreateAnalysisPlantRouting} from './useCreateAnalysisPlantRouting'
import {useCreateAnalysisSubmission} from './useCreateAnalysisSubmission'
import {useGetStoppagesSuggestions} from './useGetStoppageSuggestion'
import {useRcfaCreatePlantOptions} from './useRcfaCreatePlantOptions'

export type {StoppageOption} from './createAnalysisForm.types'

const createInitialItem = ({
  title = '',
  description = '',
  stoppageStart = null,
  stoppageEnd = null,
  equipment = ''
}: Partial<
  Pick<CreateAnalysisItem, 'title' | 'description' | 'stoppageStart' | 'stoppageEnd' | 'equipment'>
> = {}): CreateAnalysisItem => ({
  title,
  description,
  privacyStatus: AnalysisPrivacyStatus.Public,
  stoppageStart,
  stoppageEnd,
  equipment,
  owner: null,
  category: null,
  areaType: null,
  dueDate: moment().add(30, 'days')
})

export const useCreateAnalysisForm = ({
  onGoNext,
  onGoToStoppageList,
  onNavigateToPlant,
  selectedStoppage,
  analysisType,
  onAnalysisCreated
}: CreateAnalysisFormProperties) => {
  const location = useLocation()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const plantUpmId = usePlantUpmId()
  const {options: plantOptions, isLoading: isLoadingPlantOptions} = useRcfaCreatePlantOptions()
  const locationState = location.state as CreateAnalysisLocationState | null
  const initialDescription =
    selectedStoppage?.description ?? locationState?.preservedDescription ?? ''

  const [item, setItem] = useState<CreateAnalysisItem>(() =>
    createInitialItem({
      title: selectedStoppage?.title ?? '',
      description: initialDescription,
      stoppageStart: selectedStoppage?.start ?? null,
      stoppageEnd: selectedStoppage?.end ?? null,
      equipment: selectedStoppage?.equipment?.text ?? ''
    })
  )
  const [customStoppage, setCustomStoppage] = useState<StoppageCode | null>(null)
  const [isWithStoppage, setIsWithStoppage] = useState<boolean>(false)
  const [fieldsDisabled, setFieldsDisabled] = useState<boolean>(true)
  const [externalStoppage, setExternalStoppage] = useState<StoppageInfo>()
  const [stoppages, setStoppages] = useState<StoppageInfo[]>([])
  const [sOption, setSOption] = useState<StoppageOption | null>(null)

  // --- plant routing: resolves selectedPlantOption, guards access, handles cross-plant redirect ---
  const {
    availablePlantOptions,
    selectedPlantOption,
    isRoutePlantAuthorized,
    validatedPlantUpmId,
    isPlantScopedInputDisabled,
    navigateToPlant,
    goToStoppageList
  } = useCreateAnalysisPlantRouting({
    itemDescription: item.description,
    selectedStoppagePlantId: selectedStoppage?.plantId,
    plantOptions,
    isLoadingPlantOptions,
    plantUpmId,
    onNavigateToPlant,
    onGoToStoppageList,
    // reset local form state before navigating to another plant
    onBeforeNavigateToPlant: () => {
      setItem(createInitialItem({description: item.description}))
      setCustomStoppage(null)
      setIsWithStoppage(false)
      setFieldsDisabled(true)
      setExternalStoppage(undefined)
      setStoppages([])
      setSOption(null)
    }
  })

  // --- submission: validation, API call, owner management ---
  const {validationError, getErrorText, doSubmit, setOwner, isLoading, resetValidation} =
    useCreateAnalysisSubmission({
      item,
      customStoppage,
      externalStoppage,
      selectedPlantOption,
      analysisType,
      onGoNext,
      onAnalysisCreated,
      setItem,
      t
    })

  const {data: stoppagesData, isPending: isLoadingStoppages} =
    useGetStoppagesSuggestions(validatedPlantUpmId)

  useEffect(() => {
    setStoppages([])
  }, [plantUpmId])

  useEffect(() => {
    if (stoppagesData) {
      setStoppages((state) => (state.length ? [...state] : [...stoppagesData, ...state]))
    }
  }, [stoppagesData])

  useEffect(() => {
    if (selectedStoppage) {
      setExternalStoppage(selectedStoppage)
      setFieldsDisabled(false)
      setIsWithStoppage(true)
      setSOption({
        id: selectedStoppage.id,
        label: `${selectedStoppage.stoppageCode?.type} - ${
          selectedStoppage.stoppageCode?.code
        } - ${moment(selectedStoppage.start).format('DD.MM.YYYY HH:mm')} - ${moment(
          selectedStoppage.end
        ).format('DD.MM.YYYY HH:mm')}`,
        isStoppage: true
      })
      setStoppages((state) =>
        state.findIndex((s) => s.id === selectedStoppage.id) === -1
          ? [...state, selectedStoppage]
          : state
      )
    }
  }, [selectedStoppage])

  // reset validation when plant changes so stale errors don't persist
  useEffect(() => {
    resetValidation()
  }, [plantUpmId, resetValidation])

  const displayStoppageSelect = useMemo(
    () => analysisType === AnalysisType.Custom && isWithStoppage,
    [analysisType, isWithStoppage]
  )

  const stoppagesOptions = useMemo(() => {
    const customOptions: StoppageOption[] = [
      {
        label: 'Link to stoppage later',
        id: 'fill_later',
        isStoppage: false,
        icon: <Icon path={mdiClockOutline} />
      },
      {
        label: 'This RCFA does not involve a stoppage',
        id: 'no_stoppage',
        isStoppage: false,
        icon: <Icon path={mdiCancel} />
      },
      {
        label: 'Add custom stoppage',
        id: 'custom_stoppage',
        isStoppage: false,
        icon: <Icon path={mdiNoteEditOutline} />
      }
    ]

    const stoppageItems: StoppageOption[] = stoppages.map((stoppage) => ({
      label: `${stoppage.stoppageCode?.type} - ${stoppage.stoppageCode?.code} - ${moment(
        stoppage.start
      ).format('DD.MM.YYYY HH:mm')} - ${moment(stoppage.end).format('DD.MM.YYYY HH:mm')}`,
      id: stoppage.id,
      isStoppage: true
    }))

    return [...stoppageItems, ...customOptions]
  }, [stoppages])

  const fillExternalStoppage = (id: string) => {
    const stoppage = stoppages?.find((s) => s.id === id)
    if (stoppage) {
      setExternalStoppage(stoppage)
      setItem((current) => ({
        ...current,
        title: stoppage.title ?? '',
        description: stoppage.description ?? '',
        stoppageStart: stoppage.start ?? null,
        stoppageEnd: stoppage.end ?? null,
        equipment: stoppage.equipment?.text ?? ''
      }))
    }
  }

  const selectStoppage = (id: string | undefined) => {
    if (id) {
      switch (id) {
        case 'fill_later':
        case 'no_stoppage':
          setIsWithStoppage(false)
          setFieldsDisabled(false)
          setExternalStoppage(undefined)
          break
        case 'custom_stoppage':
          setIsWithStoppage(true)
          setFieldsDisabled(false)
          setExternalStoppage(undefined)
          break
        default:
          fillExternalStoppage(id)
          setFieldsDisabled(false)
          setIsWithStoppage(false)
      }
    } else {
      setFieldsDisabled(true)
      setIsWithStoppage(false)
      setExternalStoppage(undefined)
    }
  }

  return {
    t,
    item,
    setItem,
    customStoppage,
    setCustomStoppage,
    validationError,
    getErrorText,
    fieldsDisabled,
    externalStoppage,
    sOption,
    setSOption,
    stoppagesOptions,
    isRoutePlantAuthorized,
    isLoadingStoppages,
    isPlantScopedInputDisabled,
    selectedPlantOption,
    plantOptions: availablePlantOptions,
    isLoadingPlantOptions,
    navigateToPlant,
    selectStoppage,
    goToStoppageList,
    doSubmit,
    displayStoppageSelect,
    setOwner,
    isLoading
  }
}
